import { AlertCircle, Mail, Package, PoundSterling, ShoppingCart } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { API, errMsg, formatDate, money } from "@/lib/api";

const STATUS_COLOR = {
  New: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
  Prepping: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  Ready: "bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-200",
  "Out for Delivery / Collection": "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200",
  "Delivered / Collected": "bg-lime-100 text-lime-800 dark:bg-lime-950 dark:text-lime-200",
  Completed: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
  Cancelled: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
};

export function StatusBadge({ status }) {
  return <Badge className={`rounded-full border-0 ${STATUS_COLOR[status] || "bg-muted"}`} data-testid={`status-badge-${(status || "").replace(/\s+/g, "-").toLowerCase()}`}>{status}</Badge>;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(
    (silent) =>
      API.get("/admin/dashboard")
        .then((r) => setData(r.data))
        .catch((e) => { if (!silent) toast.error(errMsg(e)); }),
    [],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Live auto-refresh so stats / recent orders stay current without a manual reload.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") load(true); };
    const timer = setInterval(refresh, 5000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [load]);

  const toggleStore = async (open) => {
    setToggling(true);
    try {
      const s = await API.get("/admin/settings");
      await API.put("/admin/settings", { ...s.data, store_open: open });
      await load();
      toast.success(open ? "Online ordering is now OPEN" : "Online ordering is now CLOSED");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setToggling(false);
    }
  };

  if (!data) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>;
  }

  const stats = [
    { label: "Orders today", value: data.orders_today, icon: ShoppingCart, id: "orders-today" },
    { label: "Pending orders", value: data.pending_orders, icon: AlertCircle, id: "pending-orders" },
    { label: "Revenue today", value: money(data.revenue_today), icon: PoundSterling, id: "revenue-today" },
    { label: "Total revenue", value: money(data.revenue_total), icon: PoundSterling, id: "revenue-total" },
    { label: "Products", value: data.products, icon: Package, id: "products-count" },
    { label: "Unread enquiries", value: data.unread_enquiries, icon: Mail, id: "unread-enquiries" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-3xl tracking-tight">Dashboard</h1>
        <Card className={`border-2 ${data.store_open ? "border-green-500/50" : "border-amber-500/60"}`} data-testid="store-status-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div>
              <p className="text-sm font-semibold">{data.store_open ? "Store is OPEN" : "Store is CLOSED"}</p>
              <p className="text-xs text-muted-foreground">{data.store_open ? "Customers can place orders" : "Online ordering is disabled"}</p>
            </div>
            <Switch checked={data.store_open} onCheckedChange={toggleStore} disabled={toggling} data-testid="store-open-switch" aria-label="Toggle store open" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-stats">
        {stats.map((s) => (
          <Card key={s.id} data-testid={`stat-${s.id}`}>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-full bg-primary/10 text-primary"><s.icon className="h-5 w-5" /></div>
              <div>
                <p className="text-2xl font-semibold" data-testid={`stat-value-${s.id}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-6" data-testid="recent-orders-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl">Recent orders</h2>
            <Link to="/admin/orders" className="text-sm text-primary hover:underline" data-testid="view-all-orders-link">View all</Link>
          </div>
          {data.recent_orders.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="recent-orders-empty">No orders yet.</p>
          ) : (
            <div className="space-y-2">
              {data.recent_orders.map((o) => (
                <Link
                  key={o.id}
                  to={`/admin/orders/${o.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border hover:border-primary transition-colors"
                  data-testid={`recent-order-${o.order_number}`}
                >
                  <div>
                    <span className="font-medium">{o.order_number}</span>
                    <span className="text-muted-foreground text-sm ml-2">{o.customer?.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-semibold">{money(o.total)}</span>
                    <StatusBadge status={o.status} />
                    <span className="text-muted-foreground hidden sm:inline">{formatDate(o.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
